import React, { useEffect, useMemo, useRef } from 'react';
import { Zap, User } from 'lucide-react';
import { CannedReply } from '../../../types/models/whatsapp';

interface CannedReplyPaletteProps {
  replies: CannedReply[];
  /** Text typed after '/', used to filter. */
  filter: string;
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (reply: CannedReply) => void;
}

/**
 * Filters by shortcut first, then title and body.
 *
 * Shortcut matches rank above text matches because the shortcut is what the
 * agent is deliberately typing — a body hit on a common word should never
 * outrank the exact shortcut being spelled out.
 */
export const filterReplies = (
  replies: CannedReply[],
  filter: string
): CannedReply[] => {
  const term = filter.trim().toLowerCase();
  if (!term) return replies.slice(0, 8);

  const scored = replies
    .map((reply) => {
      const shortcut = reply.shortcut.toLowerCase();
      if (shortcut.startsWith(term)) return { reply, score: 0 };
      if (shortcut.includes(term)) return { reply, score: 1 };
      if (reply.title.toLowerCase().includes(term)) return { reply, score: 2 };
      if (reply.body.toLowerCase().includes(term)) return { reply, score: 3 };
      return null;
    })
    .filter((entry): entry is { reply: CannedReply; score: number } => entry !== null);

  // Stable within a score band: the incoming list is already ranked by useCount.
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, 8).map((entry) => entry.reply);
};

const CannedReplyPalette: React.FC<CannedReplyPaletteProps> = ({
  replies,
  filter,
  activeIndex,
  onHover,
  onSelect
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  const matches = useMemo(() => filterReplies(replies, filter), [replies, filter]);

  // Keep the keyboard-selected row in view.
  useEffect(() => {
    const active = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (matches.length === 0) {
    return (
      <div className="mb-2 rounded-lg border border-gray-200 bg-white p-3 text-center text-xs text-gray-500 shadow-lg">
        No canned reply matches “{filter}”
      </div>
    );
  }

  return (
    <div className="mb-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex items-center gap-1.5 border-b border-gray-100 px-3 py-1.5">
        <Zap className="h-3 w-3 text-primary-600" />
        <span className="text-[11px] font-medium text-gray-500">
          Canned replies — ↑↓ to navigate, Tab or Enter to insert, Esc to dismiss
        </span>
      </div>

      <ul ref={listRef} className="max-h-60 overflow-y-auto">
        {matches.map((reply, index) => (
          <li key={reply.id}>
            <button
              type="button"
              // Mouse down fires before the textarea blurs, so focus is kept.
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(reply);
              }}
              onMouseEnter={() => onHover(index)}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                index === activeIndex
                  ? 'bg-primary-50'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600">
                /{reply.shortcut}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1">
                  <span className="truncate text-xs font-medium text-gray-900">
                    {reply.title}
                  </span>
                  {reply.scope === 'personal' && (
                    <User
                      className="h-3 w-3 shrink-0 text-gray-400"
                      aria-label="Personal reply"
                    />
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[11px] text-gray-500">
                  {reply.body}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CannedReplyPalette;
